import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash, Truck } from 'lucide-react';
import { api } from '../../services/api';
import { Modal } from '../../Components/Modal/Modal';

const COMMON_BRANDS = [
  'Chevrolet', 'Renault', 'Mazda', 'Toyota', 'Nissan', 
  'Kia', 'Hyundai', 'Suzuki', 'Volkswagen', 'Ford', 
  'Honda', 'Mercedes-Benz', 'BMW', 'Foton', 'JAC'
];

const VEHICLE_TYPE_OPTIONS = ['Fuerza', 'Canasta', 'Turbo', 'Camioneta', 'Motocicleta'];

function normalizePlate(value) {
  return String(value || '').replace(/[\s.\-]+/g, '').toUpperCase();
}

// Semaforización de fechas de vencimiento: vencido (rojo), próximo a vencer
// dentro de 30 días (ámbar) o vigente (verde). Sin fecha registrada = gris.
const EXPIRY_WARNING_DAYS = 30;
function getExpiryStatus(dateStr) {
  if (!dateStr) return { text: '-', dot: 'bg-gray-300', textClass: 'text-gray-400' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return { text: dateStr, dot: 'bg-gray-300', textClass: 'text-gray-400' };
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: dateStr, dot: 'bg-red-500', textClass: 'text-red-600 font-semibold' };
  if (diffDays <= EXPIRY_WARNING_DAYS) return { text: dateStr, dot: 'bg-amber-500', textClass: 'text-amber-600 font-semibold' };
  return { text: dateStr, dot: 'bg-green-500', textClass: 'text-green-700' };
}

function ExpiryCell({ date }) {
  const s = getExpiryStatus(date);
  return (
    <span className={`inline-flex items-center gap-1.5 ${s.textClass}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      {s.text}
    </span>
  );
}

export default function VehiclesModule() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    plate: '',
    brand: '',
    model: '',
    has_documents: false,
    soat_expiry: '',
    tecnomecanica_expiry: '',
    vehicle_type: '',
    policy_expiry: '',
    izaje_carga_expiry: '',
    rigidez_dielectrica_expiry: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const res = await api.vehicles.get();
      if (res && res.ok) {
        setVehicles(res.data || []);
      }
    } catch (e) {
      console.error('Error loading vehicles', e);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData({
      plate: '',
      brand: '',
      model: '',
      has_documents: false,
      soat_expiry: '',
      tecnomecanica_expiry: '',
      vehicle_type: '',
      policy_expiry: '',
      izaje_carga_expiry: '',
      rigidez_dielectrica_expiry: ''
    });
    setError('');
    setIsModalOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setFormData({
      plate: v.plate,
      brand: v.brand,
      model: v.model,
      has_documents: v.has_documents === 1 || v.has_documents === true || v.has_documents === '1',
      soat_expiry: v.soat_expiry || '',
      tecnomecanica_expiry: v.tecnomecanica_expiry || '',
      vehicle_type: v.vehicle_type || '',
      policy_expiry: v.policy_expiry || '',
      izaje_carga_expiry: v.izaje_carga_expiry || '',
      rigidez_dielectrica_expiry: v.rigidez_dielectrica_expiry || ''
    });
    setError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este vehículo?')) return;
    try {
      const res = await api.vehicles.delete({ id });
      if (res && res.ok) {
        loadVehicles();
      } else {
        alert('Error al eliminar: ' + (res?.error || 'Desconocido'));
      }
    } catch (e) {
      alert('Error al eliminar');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.plate || !formData.brand) {
      setError('Placa y Marca son obligatorios.');
      return;
    }

    const payload = {
      ...formData,
      plate: normalizePlate(formData.plate),
      id: editing ? editing.id : undefined,
      has_documents: formData.has_documents ? 1 : 0
    };

    try {
      const res = await api.vehicles.upsert(payload);
      if (res && res.ok) {
        setIsModalOpen(false);
        loadVehicles();
      } else {
        setError(res?.error || 'Error al guardar');
      }
    } catch (e) {
      setError('Error de conexión o servidor');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
          <Truck size={20} />
          Gestión de Flota
        </h3>
        <button
          onClick={openNew}
          className="bg-senerfi-orange hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center gap-2 text-sm"
          style={{ backgroundColor: '#F07E2D' }}
        >
          <Plus size={16} />
          Nuevo Vehículo
        </button>
      </div>

      <div className="overflow-x-auto border rounded-lg bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 uppercase font-medium">
            <tr>
              <th className="px-4 py-3 text-left">Placa</th>
              <th className="px-4 py-3 text-left">Marca</th>
              <th className="px-4 py-3 text-left">Modelo</th>
              <th className="px-4 py-3 text-center">Docs</th>
              <th className="px-4 py-3 text-left">SOAT</th>
              <th className="px-4 py-3 text-left">Tecnomecánica</th>
              <th className="px-4 py-3 text-left">Izaje de carga</th>
              <th className="px-4 py-3 text-left">Rigidez dieléctrica</th>
              <th className="px-4 py-3 text-left">Tipo de vehículo</th>
              <th className="px-4 py-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="10" className="p-4 text-center text-gray-500">Cargando...</td></tr>
            ) : vehicles.length === 0 ? (
              <tr><td colSpan="10" className="p-4 text-center text-gray-500">No hay vehículos registrados.</td></tr>
            ) : (
              vehicles.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{v.plate}</td>
                  <td className="px-4 py-3">{v.brand}</td>
                  <td className="px-4 py-3">{v.model}</td>
                  <td className="px-4 py-3 text-center">
                    {(v.has_documents === 1) ? (
                      <span className="text-green-600 font-bold">Sí</span>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs"><ExpiryCell date={v.soat_expiry} /></td>
                  <td className="px-4 py-3 text-xs"><ExpiryCell date={v.tecnomecanica_expiry} /></td>
                  <td className="px-4 py-3 text-xs"><ExpiryCell date={v.izaje_carga_expiry} /></td>
                  <td className="px-4 py-3 text-xs"><ExpiryCell date={v.rigidez_dielectrica_expiry} /></td>
                  <td className="px-4 py-3 text-xs">{v.vehicle_type || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(v)} className="text-blue-600 hover:text-blue-800"><Pencil size={16}/></button>
                      <button onClick={() => handleDelete(v.id)} className="text-red-600 hover:text-red-800"><Trash size={16}/></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editing ? "Editar Vehículo" : "Nuevo Vehículo"}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placa del vehículo *</label>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={formData.plate}
              onChange={(e) => setFormData({...formData, plate: e.target.value.toUpperCase()})}
              placeholder="AAA-123"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marca *</label>
            <input
              list="brand-list"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={formData.brand}
              onChange={(e) => setFormData({...formData, brand: e.target.value})}
              required
              placeholder="Seleccione o escriba una marca..."
            />
            <datalist id="brand-list">
              {COMMON_BRANDS.map(b => (
                <option key={b} value={b} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={formData.model}
              onChange={(e) => setFormData({...formData, model: e.target.value})}
              placeholder="Ej: 2024, Aveo, etc."
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Carpeta documental</span>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="has_documents"
                  checked={formData.has_documents === true}
                  onChange={() => setFormData({...formData, has_documents: true})}
                />
                Sí
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="has_documents"
                  checked={formData.has_documents === false}
                  onChange={() => setFormData({...formData, has_documents: false})}
                />
                No
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha vigencia SOAT</label>
              <input 
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.soat_expiry}
                onChange={(e) => setFormData({...formData, soat_expiry: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha vigencia revisión tecnomecánica</label>
              <input 
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.tecnomecanica_expiry}
                onChange={(e) => setFormData({...formData, tecnomecanica_expiry: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de vehículo</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500"
              value={formData.vehicle_type}
              onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
            >
              <option value="">Seleccionar tipo de vehículo</option>
              {VEHICLE_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha vigencia póliza</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={formData.policy_expiry}
              onChange={(e) => setFormData({...formData, policy_expiry: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha vigencia prueba de izaje de carga</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.izaje_carga_expiry}
                onChange={(e) => setFormData({...formData, izaje_carga_expiry: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha vigencia prueba de rigidez dieléctrica</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.rigidez_dielectrica_expiry}
                onChange={(e) => setFormData({...formData, rigidez_dielectrica_expiry: e.target.value})}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-senerfi-orange text-white rounded hover:bg-orange-600" style={{ backgroundColor: '#F07E2D' }}>Guardar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
